import React, { useRef, useState } from 'react';
import { carteraApi } from './api';
import { buildBudgetPdfFile } from '../../Cotizador/PDFComponent/buildBudgetPdfFile';
import { Pill } from './helpers';

/**
 * Manda la cotización del lead como documento nativo de WhatsApp.
 *
 * ── El problema que resuelve ─────────────────────────────────────────────
 * Hasta ahora el lead recibía un ENLACE a `/descargar/{secret}`, que es una app de
 * React, no un PDF. Muchos leads no lo abren —o lo abren en un navegador que no
 * descarga nada— y el asesor se queda sin saber si vio la cotización. Con esto
 * llega un archivo, con su icono de PDF, dentro del chat.
 *
 * ── Los tres pasos, en orden ─────────────────────────────────────────────
 *   1. GET de los datos (autenticado, ligado a la conversación);
 *   2. `buildBudgetPdfFile` los renderiza con el MISMO `PDFComponent` del cotizador;
 *   3. POST al endpoint documental que ya usa cobranza.
 *
 * 🔴 El PDF se genera en el NAVEGADOR. El backend corre en Lambda y no tiene
 * Chromium ni Node para React PDF; por eso esto es un botón y no un envío
 * automático al cerrar el handoff.
 *
 * 🔴 Y este clic TOMA la conversación en modo humano, igual que el envío de estado
 * de cuenta: es el mismo pipeline. Se avisa en el tooltip para que el asesor no
 * apague el bot sin darse cuenta.
 *
 * @param {object} props
 * @param {object} props.conversacion  la fila de la bandeja (necesita id)
 * @param {boolean} props.habilitado   bandera del backend: features.cotizacion_pdf_manual
 * @param {boolean} props.ventanaAbierta ventana de 24 h de Meta
 * @param {boolean} props.bloqueada    conversación no activa
 * @param {boolean} props.ocupado      ya hay otro envío en curso en el hilo
 * @param {(file: File, caption: string, requestId: string) => Promise<any>} props.onEnviar
 *        el uploader del hilo; devuelve la respuesta del POST
 */
export const CotizacionPdfAction = ({
  conversacion,
  habilitado,
  ventanaAbierta,
  bloqueada,
  ocupado,
  onEnviar,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(false);
  // 🔴 El id del intento vive en un ref, no en el estado: tiene que sobrevivir a
  // los re-render de `busy` para que un reintento de axios reuse EL MISMO id y el
  // backend reconozca el duplicado. Un id nuevo por render mandaría dos PDFs.
  const requestId = useRef(null);

  // La bandera la manda el backend en la respuesta de la bandeja. Apagada, el
  // botón no existe (y el endpoint también responde 404: ver §17.4).
  if (!habilitado) return null;

  const cotizacion = conversacion?.cotizacion_actual;
  const pendienteDeConsolidar = Boolean(cotizacion?.pendiente_de_consolidar);

  const razonBloqueo = (() => {
    if (!conversacion?.id) return 'Este lead todavía no tiene conversación.';
    if (!conversacion?.budget_id) return 'El lead no tiene una cotización ligada.';
    if (bloqueada) return 'La conversación no está activa.';
    if (pendienteDeConsolidar) {
      return 'La última recotización no se ha guardado en la cotización. Consolídala antes de enviar el PDF.';
    }
    if (!ventanaAbierta) {
      return 'El envío de archivos requiere que el cliente haya escrito en las últimas 24 h.';
    }
    return null;
  })();

  const deshabilitado = Boolean(razonBloqueo) || busy || ocupado;

  const enviar = async () => {
    // Guarda contra doble clic ANTES de cualquier await: entre el GET y el POST
    // pasan segundos y ahí caben dos clics más.
    if (deshabilitado) return;
    setBusy(true);
    setError(null);
    setOk(false);

    // Un id por intento del USUARIO. Se renueva aquí y no en cada request, así un
    // reintento de red reusa el mismo y un reenvío deliberado trae uno nuevo.
    requestId.current = crearRequestId();

    try {
      const { data } = await carteraApi.getCotizacionPdfData(conversacion.id);
      const dto = data?.data;
      const archivo = await buildBudgetPdfFile(dto, dto?.filename);

      await onEnviar(archivo, dto?.caption || 'Cotización PEICAR', requestId.current);
      setOk(true);
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 10 }}>
      <Pill
        icon={busy ? 'pi-spin pi-spinner' : 'pi-whatsapp'}
        waBg="#25D366"
        disabled={deshabilitado}
        title={razonBloqueo || 'Genera la cotización vigente y la envía como documento por este chat. Toma la conversación en modo humano.'}
        onClick={enviar}
        style={{ width: '100%' }}
      >
        {busy ? 'Generando y enviando…' : 'Enviar cotización PDF'}
      </Pill>

      {razonBloqueo && !busy && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#8a6d2a', lineHeight: 1.35 }}>
          {razonBloqueo}
        </div>
      )}
      {error && (
        <div role="alert" style={{ marginTop: 6, fontSize: 11, color: '#B42318', lineHeight: 1.35 }}>
          {error}
        </div>
      )}
      {ok && !error && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#087A4D', fontWeight: 600 }}>
          Cotización PDF enviada
        </div>
      )}
    </div>
  );
};

/** UUID v4. `crypto.randomUUID` no existe en Safari viejo ni en jsdom. */
const crearRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

/**
 * Un mensaje que le diga al asesor QUÉ hacer.
 *
 * 🔴 Se distingue por `codigo`, no por el texto: los códigos son el contrato del
 * endpoint y el texto del backend puede cambiar. "Error 409" no le dice a nadie que
 * tiene que ir a consolidar una recotización.
 */
const mensajeDeError = (error) => {
  const respuesta = error?.response;
  const codigo = respuesta?.data?.codigo;

  if (codigo === 'cotizacion_no_consolidada') {
    return 'La recotización que aceptó el lead aún no se guardó en la cotización. Consolídala y vuelve a intentar.';
  }
  if (codigo === 'cotizacion_no_coincide') {
    return respuesta?.data?.message || 'La cotización guardada no coincide con la que el lead aceptó.';
  }
  if (codigo === 'datos_pdf_incompletos') {
    return 'Esta cotización no tiene datos suficientes para generar el PDF.';
  }
  if (codigo === 'cotizacion_no_encontrada' || codigo === 'cotizacion_pdf_deshabilitado') {
    return 'No hay una cotización que se pueda enviar para este lead.';
  }
  if (codigo === 'cotizacion_cambio_sin_aviso') {
    return 'La cotización cambió desde la página o el admin y el lead todavía no lo sabe. Usa «Avisarle» y después envía el PDF.';
  }
  if (codigo === 'envio_en_curso') {
    return 'Este envío ya está en curso.';
  }
  if (respuesta?.status === 422 && respuesta?.data?.errors) {
    // Validación del endpoint documental: la más común es la ventana cerrada.
    return Object.values(respuesta.data.errors).flat().join(' ');
  }
  if (!respuesta) {
    // Sin respuesta = falló el renderer o la red. El mensaje del Error propio de
    // buildBudgetPdfFile ya es específico; el de axios no.
    return error?.message && !/network|timeout/i.test(error.message)
      ? error.message
      : 'No se pudo enviar la cotización. Revisa tu conexión y vuelve a intentar.';
  }

  return respuesta?.data?.message || 'No se pudo enviar la cotización.';
};
